/**
 * @module cercaliagl/view/VWidgetRouting
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import $ from 'jquery';
import { MDCCheckbox } from '@material/checkbox';
import { MDCLinearProgress } from '@material/linear-progress';
import { MDCSwitch } from '@material/switch';
import { MDCTextField } from '@material/textfield';
import Sortable from 'sortablejs';
import BaseView, { EventType as BaseViewEventType } from './BaseView';
import { createUid, hhmmssToSeconds, normalizeTime, normalizeDistance, normalizeDistMeters, isMobile, isNumber, } from '../util';
import { createCheckboxIconInput, createCircleStop, createIconButton, createInputDate, createInputTime, createProgressBar, createRadiobox, createSwitch, createTextButton, createTextFieldWithSuffix, } from './VUtils';
import { i18n } from '../dictionary';
/**
 * @typedef {Object} RoutingOptions
 * @property {string} cmd CMD (route)
 * @property {string} weight Weight
 * @property {boolean} isLogistics Logistics route
 * @property {boolean} reorder Reorder
 * @property {string} carType Car type
 * @property {string} gasolineSelected Gasoline selected
 * @property {number} gasolinePrice Gasoline price
 * @property {number} vehicleConsumption Vehicle consumption
 * @property {string} [trstart] Time restrictions start
 * @property {string} [departuretime] Departure time
 * @property {LogisticsParams} [logisticParams] Logistics params
 * @property {boolean} [breaktime] Tacograph activated
 * @property {boolean} [twoDrivers] There are two drivers in the vehicle
 */
/**
 * @typedef {Object} LogisticsParams
 * @property {number} [vweight] Route logistics parameter. Total maximum vehicle weight.
 * @property {number} [vaxleweight] Route logistics parameter. Maximum vehicle axle weight.
 * @property {number} [vheight] Route logistics parameter. Maximum vehicle height.
 * @property {number} [vlength] Route logistics parameter. Maximum vehicle length.
 * @property {number} [vwidth] Route logistics parameter. Maximum vehicle width.
 * @property {number} [vmaxvel] Route logistics parameter. Maximum vehicle speed.
 * @property {boolean} [rimp] Route logistics parameter. Dangerous goods
 * @property {string} [trinfodate] YYYYMMDD
 * @property {number} [trinfodays] Number days to consecutives to show restrictions.
 * @property {number} [trstart] Avoid route restrictions. In timestamp format.
 * @property {boolean} [breaktime] Add breaktime to route.
 * @property {string} [adrtunnel] Set tunnel category
 */
/**
 * @typedef {Object} NewStepRouting
 * @property {number} numStep Num step
 * @property {import("../servers").GeEntity} [ge] Geo entity
 * @property {string} [formattedAddress] Formatted address
 */
/**
 * @typedef {Object} SubStageDataMouseEvent
 * @property {string} wkt WKT
 * @property {string} angle Angle
 * @property {string} type Type
 */
/**
 * @typedef {Object} StepsDraggedData
 * @property {number} newIndex New index
 * @property {number} oldIndex Old index
 */
/**
 * @typedef {Object} GasolinePrices
 * @property {string} gas95
 * @property {string} gas98
 * @property {string} diesel
 * @property {string} biodiesel
 */
/**
 * @enum {string}
 */
export var EventType = {
    ROUTE_REMOVED: 'route_removed',
    SAVE_ROUTE: 'save_route',
    PRINT_ROUTE: 'print_route',
    CLOSE_ROUTING: 'close_routing',
    CLOSE_REPORT: 'close_report',
    MINIMIZE: 'minimize',
    INVERT_ROUTE: 'invert_route',
    REORDER_STEPS: 'reorder_steps',
    STEPS_CHANGED: 'steps_changed',
    STEP_REMOVED: 'step_removed',
    OPTIONS_CHANGED: 'options_changed',
    GET_REPORT: 'get_report',
    SELECT_SUMMARY: 'select_summary',
    BACK: 'back',
    //
    TAP_SUBSTAGE: 'tap_substage',
    MOUSEENTER_SUBSTAGE: 'mousseenter_substage',
    MOUSELEAVE_SUBSTAGE: 'mousseleave_substage',
};
/**
 * @enum {string}
 */
export var Weight = {
    TIME: 'time',
    REALTIME: 'realtime',
    MONEY: 'money',
    DISTANCE: 'distance',
};
/**
 * @enum {string}
 */
var CarType = {
    CAR: 'car',
    VAN: 'van',
    LOGISTICS: 'logistics',
};
/**
 * @enum {string}
 */
var Gas = {
    GAS95: 'gas95',
    GAS98: 'gas98',
    DIESEL: 'diesel',
    BIODIESEL: 'biodiesel',
};
/**
 * @enum {string}
 */
export var LogisticsIcons = {
    VAXLEWEIGHT: '<span class="icon-vaxleweight"><span class="path1"></span><span class="path2"></span><span class="path3"></span></span>',
    VMAXVEL: '<span class="icon-velmax"><span class="path1"></span><span class="path2"></span><span class="path3"></span></span>',
    VHEIGHT: '<span class="icon-vheight"><span class="path1"></span><span class="path2"></span><span class="path3"></span><span class="path4"></span><span class="path5"></span></span>',
    VLENGTH: '<span class="icon-vlength"><span class="path1"></span><span class="path2"></span><span class="path3"></span></span>',
    VWIDTH: '<span class="icon-vwidth"><span class="path1"></span><span class="path2"></span><span class="path3"></span><span class="path4"></span><span class="path5"></span><span class="path6"></span></span>',
    VWEIGHT: '<span class="icon-lweight"><span class="path1"></span><span class="path2"></span><span class="path3"></span></span>',
    RIMP: '<span class="icon-rimp"><span class="path1"></span><span class="path2"></span><span class="path3"></span><span class="path4"></span><span class="path5"></span><span class="path6"></span></span>',
};
/**
 * @classdesc
 */
var VWidgetRouting = /** @class */ (function (_super) {
    __extends(VWidgetRouting, _super);
    /**
     * @constructor
     */
    function VWidgetRouting() {
        var _this = this;
        var jqElement = $("\n      <div class=\"v-widgetrouting\">\n        <div class=\"options mdc-elevation--z4\">\n          <div class=\"top-actions header\">\n            ".concat(createIconButton("carType ".concat(CarType.CAR, " active"), CarType.CAR, 'car'), "\n            ").concat(createIconButton("carType ".concat(CarType.VAN), CarType.VAN, CarType.VAN, 'van'), "\n            ").concat(createIconButton("carType ".concat(CarType.LOGISTICS), CarType.LOGISTICS, 'logistics'), "\n            <div class=\"closeBtn\">\n              ").concat(createIconButton('closeBtn', null, 'close'), "\n            </div>\n          </div>\n          <div class=\"search-steps\">\n            <span class=\"invert icon-invert\"></span>\n            <ul class=\"steps\"></ul>\n          </div>\n          <div class=\"settings\">\n            ").concat(createProgressBar(), "\n            <div>\n              <div class=\"left\">\n                ").concat(createIconButton('addStep', null, 'add-circle'), "\n                ").concat(createIconButton('cleanRoute', null, 'trash'), "\n                ").concat(createIconButton('saveRoute', null, 'save'), "\n                ").concat(createIconButton('printRoute', null, 'print'), "\n              </div>\n              <div class=\"right\">\n                ").concat(createIconButton('moreSettings', null, 'settings'), "\n              </div>\n              <br style=\"clear:both\"/>\n            </div>\n            <div class=\"more-settings\">\n              <div class=\"box-weight\">\n                <div class=\"settings-title\">").concat(i18n('routeType'), "</div>\n                <div class=\"radios\">\n                  <div class=\"row\">\n                    <div class='column'>\n                      ").concat(createRadiobox('weight', Weight.TIME, true, i18n('time'), true), "\n                    </div>\n                    <div class='column'>\n                      ").concat(createRadiobox('weight', Weight.DISTANCE, true, i18n('distance'), false), "\n                    </div>\n                    <div class='column'>\n                      ").concat(createRadiobox('weight', Weight.MONEY, true, i18n('avoidTolls'), false), "\n                    </div>\n                    <div class='column'>\n                      ").concat(createRadiobox('weight', Weight.REALTIME, true, i18n('realtime'), false), "\n                    </div>\n                  </div>\n                </div>\n              </div>\n              <div>\n                <div class=\"box-departureTime\">\n                  ").concat(createSwitch('departureTime', i18n('departureTime')), "\n                  <div class=\"inputs\">\n                    ").concat(createInputDate('date'), "\n                    ").concat(createInputTime('time'), "\n                  </div>\n                </div>\n                <div class=\"box-optimizedRoute\">\n                  ").concat(createSwitch('optimizedRoute', i18n('optimizedRoute')), "\n                </div>\n                <div class=\"box-addedCosts\">\n                  ").concat(createSwitch('addedCosts', i18n('addedCosts')), "\n                  <div class=\"inputs\">\n                    <div class=\"radios\">\n                      ").concat(createRadiobox('gas', Gas.GAS95, false, 'gas95', true), "\n                      ").concat(createRadiobox('gas', Gas.GAS98, false, 'gas98', false), "\n                      ").concat(createRadiobox('gas', Gas.DIESEL, false, 'diesel', false), "\n                      ").concat(createRadiobox('gas', Gas.BIODIESEL, false, 'biodiesel', false), "\n                    </div>\n                    <div class=\"price-cons\">\n                      ").concat(createTextFieldWithSuffix('consum', 'Consumption', 'l/100km', 'gasstation', '7.4'), "\n                      ").concat(createTextFieldWithSuffix('price', 'Price', '€/l', null, '1.10'), "\n                    </div>\n                  </div>\n                </div>\n                <div class=\"box-logistics\">\n                  <span class=\"switch-label\">").concat(createSwitch('roadRestrictions', i18n('roadRestrictions')), "</span>\n                  <div class=\"log-params\">\n                    <div class=\"row\">\n                      <div class='column'>\n                        ").concat(createCheckboxIconInput('vweight', i18n('maxWeight'), LogisticsIcons.VWEIGHT, false), "\n                      </div>\n                      <div class='column'>\n                        ").concat(createCheckboxIconInput('vlength', i18n('maxLength'), LogisticsIcons.VLENGTH, false), "\n                      </div>\n                    </div>\n                    <div class=\"row\">\n                      <div class='column'>\n                        ").concat(createCheckboxIconInput('vaxleweight', i18n('maxAxleWeight'), LogisticsIcons.VAXLEWEIGHT, false), "\n                      </div>\n                      <div class='column'>\n                        ").concat(createCheckboxIconInput('vwidth', i18n('maxWidth'), LogisticsIcons.VWIDTH, false), "             \n                      </div>\n                    </div>\n                    <div class=\"row\">\n                      <div class='column'>\n                        ").concat(createCheckboxIconInput('vheight', i18n('maxHeight'), LogisticsIcons.VHEIGHT, false), "\n                      </div>\n                      <div class='column'>\n                        ").concat(createCheckboxIconInput('vmaxvel', i18n('maxSpeed'), LogisticsIcons.VMAXVEL, false), "\n                      </div>\n                    </div>\n                    <div class=\"row\">\n                      <div class='column'>\n                        ").concat(createCheckboxIconInput('rimp', i18n('dangerousGoods'), LogisticsIcons.RIMP, false, false), "\n                      </div>\n                    </div>\n                    <div class=\"row\">\n                      <div class=\"box-adrTunnel\" style=\"display: none;\">\n                        <div class=\"adr-tunnel-label-container\">\n                          <span id=\"adrTunnelLabel\">TRC:</span>\n                          <div id=\"adrTunnelTooltip\" class=\"v-tooltip\">").concat(i18n('trc'), "</div>\n                        </div>\n                        <div class=\"radios\">\n                          ").concat(createRadiobox('select-adr-tunnel', '-', true, '-', true), "\n                          ").concat(createRadiobox('select-adr-tunnel', 'B', true, 'B', false), "\n                          ").concat(createRadiobox('select-adr-tunnel', 'C', true, 'C', false), "\n                          ").concat(createRadiobox('select-adr-tunnel', 'D', true, 'D', false), "\n                          ").concat(createRadiobox('select-adr-tunnel', 'E', true, 'E', false), "\n                        </div>\n                      </div>\n                    </div>\n                  </div>\n                  </div>\n                  <div class=\"box-breaktime\">\n                    <span class=\"switch-label\">").concat(createSwitch('breakTime', i18n('addBreakTime')), "</span>\n                    <div class=\"two-drivers\">\n                      <div class=\"row\">\n                        <div class='column'>\n                          ").concat(createCheckboxIconInput('twodrivers', i18n('twoDrivers'), '', false, false), "\n                        </div>\n                      </div>\n                    </div>\n                  </div>\n              </div>\n              <div class=\"settings-actions-divider\">\n                ").concat(createTextButton('refresh', i18n('refresh'), false), "\n              </div>\n            </div>\n          </div>\n        </div>\n        <div class=\"summary\">\n        </div>\n        <div class=\"report mdc-elevation--z4\">\n          <div class=\"routingstops\">\n            <span class=\"btn-icon icon-back\"></span>\n            <span class=\"btn-icon icon-print\"></span>\n            <span class=\"btn-icon icon-save\"></span>\n            <ul class=\"stops\"></ul>\n            <div class=\"timedist\">\n              <div class=\"dist\">\n                <span class=\"icon icon-markers\"></span>\n                <span class=\"text\">???Km</span>              \n              </div>\n              <div class=\"time\">\n                <span class=\"icon icon-timer\"></span>\n                <span class=\"text\">???min</span>\n              </div>\n              <div class=\"breaktime\">\n                <span class=\"icon icon-resttime\"></span>\n                <span class=\"text\">???min</span>\n              </div>\n              <div class=\"traffic\">\n                <span class=\"icon icon-alert\"></span>\n                <span class=\"text\">???min traffic delay</span>\n              </div>\n            </div>\n            <div class=\"costs\">\n              <div class=\"motorway\">\n                <span class=\"icon icon-motorway\"></span>\n                <span class=\"text\">???\u20AC</span>\n              </div>\n              <div class=\"gas\">\n                <span class=\"icon icon-diesel\"></span>\n                <span class=\"text\">????\u20AC</span>\n              </div>\n            </div>\n          </div>\n          <ul class=\"routingsteps mdc-list collection\">\n          </ul>\n        </div>"));
        _this = _super.call(this, jqElement) || this;
        /**
         * @private
         * @type {JQuery}
         */
        _this.jqOptions_ = _this.jqElement_.find('.options');
        var resizeObserver = new ResizeObserver(function (entries) {
            var changes = entries[0];
            var optionsMaxHeight = Number(_this.jqOptions_.css('max-height').replace('px', ''));
            var suggestions = _this.jqElement_.find('.suggestions');
            if (changes.contentRect.height >= optionsMaxHeight) {
                _this.jqOptions_.css('overflow-y', 'scroll');
                suggestions.css('width', '100%');
            }
            else {
                _this.jqOptions_.css('overflow-y', 'unset');
                suggestions.css('width', 'unset');
            }
        });
        resizeObserver.observe(_this.jqOptions_[0]);
        /**
         * @private
         * @type {JQuery}
         */
        _this.jqStepList_ = _this.jqElement_.find('ul.steps');
        // eslint-disable-next-line no-new
        var originalOrder = [];
        var sortable = new Sortable(_this.jqStepList_[0], {
            animation: 150,
            delay: 200,
            delayOnTouchOnly: true,
            onChoose: function () {
                originalOrder = sortable.toArray();
            },
            onEnd: function (evt) {
                var oldIndexElement = _this.getElementByNumber_(evt.oldIndex);
                var newIndexElement = _this.getElementByNumber_(evt.newIndex);
                sortable.sort(originalOrder);
                if (!oldIndexElement.querySelector('input').value || !newIndexElement.querySelector('input').value) {
                    if (_this.numSteps_ === 0) {
                        _this.emit(EventType.INVERT_ROUTE);
                    }
                }
                else {
                    _this.emit(EventType.REORDER_STEPS, /** @type {NewStepRouting} */ ({ oldIndex: evt.oldIndex, newIndex: evt.newIndex }));
                }
            },
        });
        /**
         * @private
         * @type {JQuery}
         */
        _this.jqSummary_ = _this.jqElement_.find('div.summary');
        /**
         * @private
         * @type {JQuery}
         */
        _this.jqReport_ = _this.jqElement_.find('div.report');
        /**
         * @private
         * @type {JQuery}
         */
        _this.jqReportStops_ = _this.jqReport_.find('.routingstops');
        /**
         * @private
         * @type {JQuery}
         */
        _this.jqReportSteps_ = _this.jqReport_.find('.routingsteps');
        /**
         * @private
         * @type {JQuery}
         */
        _this.jqTooltip_ = $(_this.jqElement_.find('#adrTunnelTooltip'));
        /**
         * @private
         * @type {number}
         */
        _this.numSteps_ = 0;
        /**
         * @private
         * @type {boolean}
         */
        _this.routeDrawn_ = false;
        /**
         * @private
         * @type {GasolinePrices}
         */
        _this.gasolinePrices_ = {
            gas95: '1.389',
            gas98: '1.389',
            diesel: '1.289',
            biodiesel: '1.349',
        };
        /**
         * @private
         * @type {RoutingOptions}
         */
        _this.routingOptions_ = {
            cmd: 'route',
            weight: 'time',
            isLogistics: false,
            breaktime: false,
            reorder: false,
            carType: CarType.CAR,
            gasolineSelected: Gas.DIESEL,
            gasolinePrice: parseFloat(_this.gasolinePrices_.diesel),
            vehicleConsumption: 7,
            twoDrivers: false,
        };
        /**
         * @private
         * @type {LogisticsParams}
         */
        _this.logisticsParams_ = {
            adrtunnel: '-',
        };
        /**
         * @private
         * @type {MDCSwitch}
         */
        _this.mdcDepartureTimeSwitch_ = null;
        /**
         * @private
         * @type {MDCSwitch}
         */
        _this.mdcOptimizedRoute_ = null;
        /**
         * @private
         * @type {MDCCheckbox}
         */
        _this.mdcRimp_ = null;
        /**
         * @private
         * @type {MDCSwitch}
         */
        _this.mdcAddedCosts_ = null;
        /**
         * @private
         * @type {MDCSwitch}
         */
        _this.mdcLogisticRestrictions_ = null;
        /**
         * @private
         * @type {MDCSwitch}
         */
        _this.mdcLogisticBreakTime_ = null;
        /**
         * @private
         * @type {MDCCheckbox}
         */
        _this.mdcLogisticTwoDrivers_ = null;
        /**
         * @private
         * @type {MDCTextField}
         */
        _this.mdcInputDepartureDateDate_ = null;
        /**
         * @private
         * @type {MDCTextField}
         */
        _this.mdcInputDepartureDateTime_ = null;
        /**
         * @private
         * @type {MDCTextField}
         */
        _this.mdcInputConsum_ = null;
        /**
         * @private
         * @type {MDCTextField}
         */
        _this.mdcInputGasPrice_ = null;
        _this.initialize_();
        return _this;
    }
    /**
     * @private
     */
    VWidgetRouting.prototype.initialize_ = function () {
        this.initializeSteps_();
        this.initializeElements_();
    };
    /**
     * @private
     */
    VWidgetRouting.prototype.initializeSteps_ = function () {
        this.numSteps_ = 0;
        var jqHtml = $("\n      <li class=\"stepOrigin\">\n        ".concat(createCircleStop('A'), "\n        <div id=\"origAutocomplete").concat(createUid(), "\" class=\"origin stepAutocomplete\"></div>\n      </li>\n      <li class=\"stepDest\">\n        ").concat(createCircleStop('B'), "\n        <div id=\"destAutocomplete").concat(createUid(), "\" class=\"destination stepAutocomplete\"></div>\n      </li>\n    "));
        this.jqStepList_.empty();
        this.jqStepList_.append(jqHtml);
    };
    /**
     * @param {import("../servers").GeEntity} [ge] Geo entity
     * @param {string} [formattedAddress] Formatted address
     */
    VWidgetRouting.prototype.addStep = function (ge, formattedAddress) {
        var autocompleteId = "stepAutocomplete".concat(createUid());
        if (this.numSteps_ === 0 || (this.numSteps_ > 0 && this.jqElement_.find(".waypoint.step".concat(this.numSteps_ - 1, " input")).val().length > 0)) {
            this.numSteps_ += 1;
            this.jqStepList_
                .find('> li')
                .last()
                .before($("<li class=\"waypoint step".concat(this.numSteps_ - 1, "\">\n            ").concat(createCircleStop("".concat(this.numSteps_)), "\n            <div id=\"").concat(autocompleteId, "\" class=\"stepAutocomplete\"></div>\n          </li>")));
            this.reorderDomElements_();
            this.emit(EventType.STEPS_CHANGED, /** @type {NewStepRouting} */ ({ numStep: this.numSteps_ - 1, ge: ge, formattedAddress: formattedAddress }));
            this.jqElement_.find('span.invert').hide();
        }
    };
    /**
     * @return {Element} Element
     */
    VWidgetRouting.prototype.getAutocompleteOrigin = function () {
        return this.jqStepList_.find('li div.origin').get(0);
    };
    /**
     * @return {Element} Element
     */
    VWidgetRouting.prototype.getAutocompleteDestination = function () {
        return this.jqStepList_.find('li div.destination').get(0);
    };
    /**
     * @param {number} num Number
     * @return {Element} Element
     * @private
    */
    VWidgetRouting.prototype.getElementByNumber_ = function (num) {
        if (num === 0) {
            return this.getAutocompleteOrigin();
        }
        if (num === this.numSteps_ + 1) {
            return this.getAutocompleteDestination();
        }
        return this.getAutocompleteByStepNum(num - 1);
    };
    /**
     * @param {number} stepNum Step num
     * @return {Element} Element
     */
    VWidgetRouting.prototype.getAutocompleteByStepNum = function (stepNum) {
        return this.jqStepList_.find("li.step".concat(stepNum, " .stepAutocomplete")).get(0);
    };
    /**
     * Remove all steps
     */
    VWidgetRouting.prototype.removeAllSteps = function () {
        this.jqStepList_.find('li.waypoint').remove();
        this.jqElement_.find('span.invert').show();
        this.numSteps_ = 0;
    };
    /**
     * @private
     * @param {number} stepIndex Index
     * @param {boolean} emitEvent Emit event
     */
    VWidgetRouting.prototype.removeStep_ = function (stepIndex, emitEvent) {
        this.jqStepList_.find("li.waypoint:eq(".concat(stepIndex, ")")).remove();
        this.numSteps_ -= 1;
        this.reorderDomElements_();
        if (emitEvent) {
            this.emit(EventType.STEP_REMOVED, stepIndex);
        }
        if (this.numSteps_ === 0) {
            this.jqElement_.find('span.invert').show();
        }
    };
    /**
     * @param {number} stepIndex Step index
     * @param {boolean} [emitEvent] Emit event
     */
    VWidgetRouting.prototype.removeStepIndex = function (stepIndex, emitEvent) {
        if (emitEvent === void 0) { emitEvent = true; }
        this.removeStep_(stepIndex, emitEvent);
    };
    /**
     * @private
     */
    VWidgetRouting.prototype.initializeElements_ = function () {
        var _this = this;
        if (!this.jqElement_) {
            return;
        }
        var divDepartureTime = this.jqElement_.find('.box-departureTime');
        if (!divDepartureTime || divDepartureTime.length === 0) {
            return;
        }
        // Buttons
        this.jqElement_.find('button.closeBtn').click(function () { return _this.emit(EventType.CLOSE_ROUTING); });
        this.jqElement_.find('button.addStep').click(function () { return _this.addStep(); }); // Button add step
        this.jqElement_.find('button.cleanRoute').click(function () { return _this.clear(); }); // Button clear route
        this.jqElement_.find('button.saveRoute').click(function () { return _this.emit(EventType.SAVE_ROUTE); }); // Button save route
        this.jqElement_.find('button.printRoute').click(function () { return _this.emit(EventType.PRINT_ROUTE); }); // Button print route
        this.jqElement_.find('button.moreSettings').click(function () { return _this.toggleSettings(); }); // Button settings
        this.jqElement_
            .find('button.refresh')
            .hide()
            .click(function () { return _this.emit(EventType.OPTIONS_CHANGED, _this.routingOptions_); }); // Button Refresh
        this.jqElement_.find('span.invert').click(function () { return _this.emit(EventType.INVERT_ROUTE); }); // Button invert
        // Car type buttons
        this.jqElement_.find('button.carType').click(function (e) {
            var buttonClicked = e.currentTarget;
            var jqBoxLogistics = _this.jqElement_.find('.box-logistics');
            var jqBoxBreakTime = _this.jqElement_.find('.box-breaktime');
            _this.routingOptions_.carType = buttonClicked.name;
            _this.routingOptions_.isLogistics = _this.routingOptions_.carType !== CarType.CAR;
            _this.jqElement_.find('button.carType').removeClass('active');
            $(buttonClicked).addClass('active');
            _this.toggleLogistics_();
            if (_this.routingOptions_.isLogistics) {
                jqBoxLogistics.show();
                jqBoxBreakTime.show();
                _this.routingOptions_.logisticParams = _this.logisticsParams_;
                if (_this.routingOptions_.logisticParams.rimp) {
                    _this.setWeight(Weight.TIME);
                    _this.toggleRadioButtonseDisabledlyExceptTime(true);
                    _this.disableAndHideDepartureTime_();
                }
            }
            else {
                jqBoxLogistics.hide();
                jqBoxBreakTime.hide();
                _this.toggleRadioButtonseDisabledlyExceptTime(false);
                _this.enableAndShowDepartureTime_();
                _this.routingOptions_.logisticParams = undefined;
            }
            if (_this.routeDrawn_) {
                _this.toggleSettings(true);
            }
        });
        // Weight radio buttons
        this.jqElement_.find('input[name=weight]').change(function (e) {
            _this.setWeight(e.target.value);
        });
        // Report routingstops section (buttons)
        this.jqReportStops_.find('.icon-back').click(function () {
            _this.setReport(null);
            _this.jqSummary_.show();
            _this.emit(EventType.CLOSE_REPORT);
        });
        this.jqReportStops_.find('.icon-print').click(function () { return _this.emit(EventType.PRINT_ROUTE); });
        this.jqReportStops_.find('.icon-save').click(function () { return _this.emit(EventType.SAVE_ROUTE); });
        this.on(BaseViewEventType.ATTACHED, function () {
            if (!_this.jqElement_) {
                return;
            }
            _this.adjustByDevice();
            var jqDepartureTimeSwitch = _this.jqElement_.find('.mdc-switch.departureTime');
            if (!jqDepartureTimeSwitch || jqDepartureTimeSwitch.length === 0) {
                return;
            }
            _this.mdcDepartureTimeSwitch_ = new MDCSwitch(jqDepartureTimeSwitch.get(0));
            _this.mdcOptimizedRoute_ = new MDCSwitch(_this.jqElement_.find('.mdc-switch.optimizedRoute').get(0));
            _this.mdcAddedCosts_ = new MDCSwitch(_this.jqElement_.find('.mdc-switch.addedCosts').get(0));
            _this.mdcLogisticRestrictions_ = new MDCSwitch(_this.jqElement_.find('.mdc-switch.roadRestrictions').get(0));
            _this.mdcLogisticBreakTime_ = new MDCSwitch(_this.jqElement_.find('.mdc-switch.breakTime').get(0));
            _this.mdcLogisticTwoDrivers_ = new MDCCheckbox(_this.jqElement_.find('.mdc-form-field.twodrivers').get(0));
            _this.mdcInputDepartureDateDate_ = new MDCTextField(_this.jqElement_.find('.box-departureTime .mdc-text-field.date').get(0));
            _this.mdcInputDepartureDateTime_ = new MDCTextField(_this.jqElement_.find('.box-departureTime .mdc-text-field.time').get(0));
            _this.mdcInputConsum_ = new MDCTextField(_this.jqElement_.find('.price-cons .mdc-text-field.consum').get(0));
            _this.mdcInputGasPrice_ = new MDCTextField(_this.jqElement_.find('.price-cons .mdc-text-field.price').get(0));
            _this.mdcDepartureTimeSwitch_.listen('click', function () {
                if (_this.mdcDepartureTimeSwitch_.selected) {
                    divDepartureTime.find('.inputs').show();
                    _this.initializeDepartureTime_(_this.mdcInputDepartureDateDate_, _this.mdcInputDepartureDateTime_);
                }
                else {
                    divDepartureTime.find('.inputs').hide();
                    _this.routingOptions_.departuretime = undefined;
                }
            });
            _this.mdcInputDepartureDateDate_.listen('change', function () {
                _this.handleChangeDepartureTime_(_this.mdcInputDepartureDateDate_.value, _this.mdcInputDepartureDateTime_.value);
            });
            _this.mdcInputDepartureDateTime_.listen('change', function () {
                _this.handleChangeDepartureTime_(_this.mdcInputDepartureDateDate_.value, _this.mdcInputDepartureDateTime_.value);
            });
            _this.mdcOptimizedRoute_.listen('click', function () {
                _this.setReorder(_this.mdcOptimizedRoute_.selected);
            });
            _this.mdcLogisticRestrictions_.listen('click', function () { return _this.toggleLogistics_(_this.mdcLogisticRestrictions_); });
            _this.mdcLogisticBreakTime_.listen('click', function () { return _this.toggleBreakTime_(_this.mdcLogisticBreakTime_); });
            _this.mdcLogisticTwoDrivers_.listen('change', function () { return _this.toggleTwoDrivers_(_this.mdcLogisticTwoDrivers_); });
            _this.jqElement_.find('input[name=select-adr-tunnel]').change(function (e) {
                _this.routingOptions_.logisticParams.adrtunnel = e.target.value;
            });
            _this.jqElement_.find('input[name=gas]').change(function (e) {
                _this.routingOptions_.gasolineSelected = e.target.value;
                _this.routingOptions_.gasolinePrice = parseFloat(_this.gasolinePrices_[_this.routingOptions_.gasolineSelected]);
            }); // gasoline type
            _this.mdcAddedCosts_.listen('click', function () {
                _this.jqElement_.find('.box-addedCosts .inputs').toggle();
                if (_this.mdcAddedCosts_.selected) {
                    _this.routingOptions_.gasolineSelected = _this.jqElement_.find('input[name=gas]:checked').val();
                    _this.routingOptions_.logisticParams.adrtunnel = _this.jqElement_.find('input[name=select-adr-tunnel]:checked').val();
                }
                else {
                    _this.routingOptions_.gasolineSelected = null;
                }
            });
            _this.mdcInputConsum_.listen('change', function () {
                _this.routingOptions_.vehicleConsumption = isNumber(_this.mdcInputConsum_.value) ? parseFloat(_this.mdcInputConsum_.value) : 7;
            });
            _this.mdcInputGasPrice_.listen('change', function () {
                _this.routingOptions_.gasolinePrice = isNumber(_this.mdcInputGasPrice_.value) ? parseFloat(_this.mdcInputGasPrice_.value) : 1.21;
            });
            _this.jqTooltip_.hide();
            _this.jqElement_.find('#adrTunnelLabel').on('mouseover', function () { return _this.jqTooltip_.show(); });
            _this.jqElement_.find('#adrTunnelLabel').on('mouseleave', function () { return _this.jqTooltip_.hide(); });
            _this.jqTooltip_.on('mouseleave', function () { return _this.jqTooltip_.hide(); });
            // Initialize fields
            _this.initializeLogisticsParams_();
            _this.initializeBreakTimeParams_();
        });
        new MDCLinearProgress(this.jqElement_.find('.progressbar').get(0)); // eslint-disable-line no-new
    };
    /**
     * Resize
     */
    VWidgetRouting.prototype.adjustByDevice = function () {
        var viewport = this.getMapViewport();
        this.jqElement_.css('width', !isMobile() ? 'auto' : viewport.offsetWidth - 10);
        // Settings
        // const jqMoreSettings = this.jqElement_.find('.more-settings');
        // jqMoreSettings.css('max-height', viewport.offsetHeight - 360);
        // Report
        this.adjustReportSize_();
        // Options
        this.adjustOptionsSize_();
    };
    /**
     * @param {string} weight Weight
     */
    VWidgetRouting.prototype.setWeight = function (weight) {
        this.routingOptions_.weight = weight;
        this.jqElement_.find('input[name=weight]').filter("[value=".concat(weight, "]")).prop('checked', true);
        var divDepartureTime = this.jqElement_.find('.box-departureTime');
        if ([Weight.TIME, Weight.MONEY].indexOf(weight) === -1) {
            this.routingOptions_.departuretime = null;
            if (this.mdcDepartureTimeSwitch_) {
                this.mdcDepartureTimeSwitch_.selected = false;
                divDepartureTime.hide();
                divDepartureTime.find('.inputs').hide();
                this.routingOptions_.departuretime = undefined;
            }
        }
        else {
            divDepartureTime.show();
        }
    };
    /**
     * @param {boolean} reorder Reorder
     */
    VWidgetRouting.prototype.setReorder = function (reorder) {
        this.routingOptions_.reorder = reorder;
        this.mdcOptimizedRoute_.selected = reorder;
    };
    /**
     * @param {boolean} [show] Show or not
     */
    VWidgetRouting.prototype.toggleSettings = function (show) {
        var jqMoreSettings = this.jqElement_.find('.more-settings');
        if (show === undefined) {
            jqMoreSettings.toggle();
        }
        else if (show === true) {
            jqMoreSettings.show();
        }
        else {
            jqMoreSettings.hide();
        }
    };
    /**
     * @private
     */
    VWidgetRouting.prototype.toggleLogistics_ = function () {
        var divLogisticsParams = this.jqElement_.find('.log-params');
        if (this.mdcLogisticRestrictions_ && this.mdcLogisticRestrictions_.selected) {
            divLogisticsParams.show();
            this.routingOptions_.logisticParams = this.logisticsParams_;
        }
        else {
            divLogisticsParams.hide();
            if (this.routingOptions_.logisticParams) {
                this.jqElement_.find('.mdc-form-field.rimp input').prop('checked', false);
                this.hideAdrTunnel_();
                this.toggleRadioButtonseDisabledlyExceptTime(false);
            }
            this.routingOptions_.logisticParams = undefined;
        }
    };
    /**
     * @private
     */
    VWidgetRouting.prototype.toggleBreakTime_ = function () {
        var breakTimeChecked = this.mdcLogisticBreakTime_.selected;
        this.routingOptions_.breaktime = breakTimeChecked;
        var divBreakTimeParams = this.jqElement_.find('.two-drivers');
        if (breakTimeChecked) {
            divBreakTimeParams.show();
            this.routingOptions_.twoDrivers = this.mdcLogisticTwoDrivers_.checked;
        }
        else {
            divBreakTimeParams.hide();
            this.mdcLogisticTwoDrivers_.checked = false;
            this.routingOptions_.twoDrivers = false;
        }
    };
    /**
     * @private
     */
    VWidgetRouting.prototype.toggleTwoDrivers_ = function () {
        if (this.mdcLogisticBreakTime_.selected) {
            this.routingOptions_.twoDrivers = this.mdcLogisticTwoDrivers_.checked;
        }
        else {
            this.routingOptions_.twoDrivers = false;
        }
    };
    /**
     * @private
     */
    VWidgetRouting.prototype.initializeLogisticsParams_ = function () {
        var _this = this;
        var jqDivLogisticsParams = this.jqElement_.find('.log-params');
        jqDivLogisticsParams.find('.mdc-text-field').each(function (i, textField) {
            var mdcTextField = new MDCTextField(textField);
            var mdcCheckbox = new MDCCheckbox(textField.parentElement.querySelector('.mdc-checkbox'));
            $(textField)
                .find('input')
                .on('input', function (e) {
                var _a;
                if ((_a = e.target.value) === null || _a === void 0 ? void 0 : _a.includes(',')) {
                    e.target.value = e.target.value.replace(',', '.');
                }
                _this.logisticsParams_[e.target.name] = mdcCheckbox.checked && e.target.value.length > 0 ? e.target.value : undefined;
            })
                .click(function (e) {
                mdcCheckbox.checked = !mdcCheckbox.checked;
                _this.logisticsParams_[e.target.name] = mdcCheckbox.checked && e.target.value.length > 0 ? e.target.value : undefined;
            });
            mdcCheckbox.listen('change', function (e) {
                _this.logisticsParams_[e.target.name] = mdcCheckbox.checked ? mdcTextField.value : undefined;
            });
        });
        this.mdcRimp_ = new MDCCheckbox(jqDivLogisticsParams.find('.mdc-form-field.rimp').get(0));
        this.mdcRimp_.listen('change', function () {
            _this.logisticsParams_.rimp = _this.mdcRimp_.checked;
            if (_this.mdcRimp_.checked) {
                _this.setWeight(Weight.TIME);
                _this.toggleRadioButtonseDisabledlyExceptTime(true);
                _this.disableAndHideDepartureTime_();
                _this.showAdrTunnel_();
            }
            else {
                _this.toggleRadioButtonseDisabledlyExceptTime(false);
                _this.enableAndShowDepartureTime_();
                _this.hideAdrTunnel_();
            }
        });
    };
    /**
     * @private
     */
    VWidgetRouting.prototype.initializeBreakTimeParams_ = function () {
        this.toggleBreakTime_();
    };
    /**
     * @private
     * @param {boolean} [enable] Enable or disable
    */
    VWidgetRouting.prototype.toggleRadioButtonseDisabledlyExceptTime = function (enable) {
        this.jqElement_.find('input[name=weight]').filter("[value=".concat(Weight.DISTANCE, "]")).prop('disabled', enable);
        this.jqElement_.find('input[name=weight]').filter("[value=".concat(Weight.MONEY, "]")).prop('disabled', enable);
        this.jqElement_.find('input[name=weight]').filter("[value=".concat(Weight.REALTIME, "]")).prop('disabled', enable);
    };
    /**
     * @private
    */
    VWidgetRouting.prototype.disableAndHideDepartureTime_ = function () {
        this.routingOptions_.departuretime = null;
        if (this.mdcDepartureTimeSwitch_) {
            var divDepartureTime = this.jqElement_.find('.box-departureTime');
            this.mdcDepartureTimeSwitch_.selected = false;
            divDepartureTime.hide();
            divDepartureTime.find('.inputs').hide();
            this.routingOptions_.departuretime = undefined;
        }
    };
    /**
     * @private
    */
    VWidgetRouting.prototype.enableAndShowDepartureTime_ = function () {
        var divDepartureTime = this.jqElement_.find('.box-departureTime');
        divDepartureTime.show();
    };
    /**
     * @private
    */
    VWidgetRouting.prototype.hideAdrTunnel_ = function () {
        var divAdrTunnel = this.jqElement_.find('.box-adrTunnel');
        if (divAdrTunnel) {
            divAdrTunnel.hide();
        }
    };
    /**
     * @private
    */
    VWidgetRouting.prototype.showAdrTunnel_ = function () {
        var divAdrTunnel = this.jqElement_.find('.box-adrTunnel');
        if (divAdrTunnel) {
            divAdrTunnel.show();
        }
    };
    /**
     * Clear all
     */
    VWidgetRouting.prototype.clear = function () {
        this.emit(EventType.ROUTE_REMOVED);
    };
    /**
     * @return {RoutingOptions} Routing options
     */
    VWidgetRouting.prototype.getOptions = function () {
        return this.routingOptions_;
    };
    VWidgetRouting.prototype.setOptions = function (options) {
        this.routingOptions_ = __assign(__assign({}, this.routingOptions_), options);
        if (options.carType) {
            var carTypeButton = this.jqElement_.find("button.carType.".concat(options.carType));
            if (carTypeButton) {
                carTypeButton.trigger('click');
            }
        }
        if (options.departuretime) {
            this.mdcDepartureTimeSwitch_.selected = options.departuretime !== undefined;
            this.mdcDepartureTimeSwitch_.emit('click');
            this.mdcInputDepartureDateDate_.value = options.departuretime ? options.departuretime.split('T')[0] : '';
            this.mdcInputDepartureDateTime_.value = options.departuretime ? options.departuretime.split('T')[1].substring(0, 5) : '';
        }
        this.mdcOptimizedRoute_.selected = options.reorder;
        this.mdcOptimizedRoute_.emit('click');
        if (options.breaktime)
            this.mdcLogisticTwoDrivers_.checked = options.twoDrivers;
        this.mdcLogisticBreakTime_.selected = options.breaktime;
        this.mdcLogisticBreakTime_.emit('click');
        if (options.gasolineSelected) {
            this.mdcAddedCosts_.selected = options.gasolineSelected;
            this.mdcAddedCosts_.emit('click');
            if (options.vehicleConsumption) {
                this.mdcInputConsum_.value = options.vehicleConsumption;
            }
            if (options.gasolinePrice) {
                this.mdcInputGasPrice_.value = options.gasolinePrice;
            }
        }
        this.jqElement_.find('input[name=gas]').filter("[value=".concat(options.gasolineSelected, "]")).prop('checked', true);
        this.jqElement_.find('input[name=weight]').filter("[value=".concat(options.weight, "]")).prop('checked', true);
        if (options.isLogistics) {
            this.mdcLogisticRestrictions_.selected = options.isLogistics;
            this.mdcLogisticRestrictions_.emit('click');
            if (options.logisticParams) {
                if (options.logisticParams.rimp || options.logisticParams.adrtunnel) {
                    this.mdcRimp_.checked = true;
                    this.jqElement_.find('input[name=select-adr-tunnel]').filter("[value=".concat(options.logisticParams.adrtunnel, "]")).prop('checked', true);
                    this.mdcRimp_.emit('change');
                }
                this.logisticsParams_ = options.logisticParams;
                this.routingOptions_.logisticParams = this.logisticsParams_;
                this.updateLogisticsParams(options.logisticParams);
            }
        }
    };
    /**
     * @private
     * @param {MDCTextField} mdcInputDate Input date
     * @param {MDCTextField} mdcInputTime Input time
     */
    VWidgetRouting.prototype.initializeDepartureTime_ = function (mdcInputDate, mdcInputTime) {
        var dateIsoString = new Date().toISOString();
        var arrDateISO = dateIsoString.split('T');
        /* eslint-disable no-param-reassign */
        /* eslint-disable prefer-destructuring */
        mdcInputDate.value = arrDateISO[0];
        mdcInputTime.value = arrDateISO[1].substring(0, 5);
        /* eslint-enable no-param-reassign */
        /* eslint-enable prefer-destructuring */
        this.routingOptions_.departuretime = dateIsoString;
    };
    /**
     * @private
     * @param {string} date Date format YYYY-MM-DD
     * @param {string} time Time HH:SS
     */
    VWidgetRouting.prototype.handleChangeDepartureTime_ = function (date, time) {
        /* eslint-disable no-param-reassign */
        /* eslint-disable prefer-destructuring */
        if (date.length === 0) {
            date = new Date().toISOString().split('T')[0];
        }
        if (time.length === 0) {
            time = '00:00';
        }
        /* eslint-enable no-param-reassign */
        /* eslint-enable prefer-destructuring */
        var currentDate = new Date(date);
        var _a = time.split(':'), hour = _a[0], minutes = _a[1];
        currentDate.setHours(parseInt(hour, 10));
        currentDate.setMinutes(parseInt(minutes, 10));
        this.routingOptions_.departuretime = currentDate.toISOString();
    };
    /**
     * @private
     */
    VWidgetRouting.prototype.reorderDomElements_ = function () {
        this.sortCircleSteps_();
        this.reinitializeStepsEvents_();
    };
    /**
     * @private
     */
    VWidgetRouting.prototype.sortCircleSteps_ = function () {
        var jqStopCirclesSpan = this.jqStepList_.find('.stop-circle span');
        var numCircles = jqStopCirclesSpan.length;
        jqStopCirclesSpan.each(function (index, stopCircle) {
            var char;
            if (index === 0) {
                char = 'A';
            }
            else if (index === numCircles - 1) {
                char = 'B';
            }
            else {
                char = "".concat(index);
            }
            stopCircle.innerHTML = char; // eslint-disable-line no-param-reassign
        });
    };
    /**
     * @private
     */
    VWidgetRouting.prototype.reinitializeStepsEvents_ = function () {
        var _this = this;
        var jqListLi = this.jqStepList_.find('> li');
        jqListLi.unbind('mouseleave').unbind('mouseenter').removeClass('waypoint');
        jqListLi.find('span.remove-step').remove();
        // Add waypoint class only for waypoints
        jqListLi.each(function (index, liElem) {
            var jqLiElem = $(liElem);
            if (index !== 0 && index !== jqListLi.length - 1) {
                var jqCloseElem_1 = $('<span class="remove-step icon-close" />')
                    .hide()
                    .click(function () { return _this.removeStep_(index - 1, true); });
                jqLiElem
                    .removeClass()
                    .addClass("waypoint step".concat(index - 1))
                    .append(jqCloseElem_1)
                    .mouseenter(function () { return jqCloseElem_1.show(); })
                    .mouseleave(function () { return jqCloseElem_1.hide(); });
            }
        });
    };
    /**
     * @param {Array.<import("../MapRouting").RouteSummary>|import("../servers.js").RoutingResultRoute|null} summaries Summary data
     * @private
     */
    VWidgetRouting.prototype.getTimeToShowFromSummary = function (summary) {
        if (this.mdcLogisticBreakTime_.selected) {
            return summary.totaltime;
        }
        if (this.mdcDepartureTimeSwitch_.selected || (this.mdcLogisticRestrictions_.selected && this.logisticsParams_.rimp) || this.logisticsParams_.vmaxvel) {
            return summary.time;
        }
        return summary.realtime;
    };
    /**
     * @param {Array.<import("../MapRouting").RouteSummary>|null} summaries Summary data
     */
    VWidgetRouting.prototype.setSummary = function (summaries) {
        var _this = this;
        if (summaries !== null) {
            if (this.mdcLogisticBreakTime_.selected) {
                this.jqElement_.find('.breaktime').show();
            }
            else {
                this.jqElement_.find('.breaktime').hide();
            }
            this.jqSummary_.empty().show();
            summaries.forEach(function (summary, index) {
                var _a;
                var timeToshow = _this.getTimeToShowFromSummary(summary);
                var showWithoutTraffic = !_this.mdcDepartureTimeSwitch_.selected && (!_this.mdcLogisticBreakTime_.selected || !((_a = _this.logisticsParams_) === null || _a === void 0 ? void 0 : _a.rimp)) && _this.routingOptions_.weight !== Weight.REALTIME;
                var jqRoute = $("\n          <div class=\"route-alt ".concat(index === 0 ? 'active' : '', " mdc-elevation--z4\" data-route=\"").concat(index, "\">\n            <div class=\"time-dist-vals\">\n              <div class=\"realtime\">").concat(normalizeTime(timeToshow), "</div>\n              <div class=\"dist secondary\">").concat(normalizeDistance(summary.dist), "</div>\n            </div>\n            <div>\n              <div class=\"route-mode\">").concat(_this.routingOptions_.carType === CarType.CAR ? i18n('routeCar') : i18n('routeTruck'), "</div>\n              <div class=\"costs secondary\">\n                <span><i class=\"icon icon-motorway\"></i>").concat(_this.getTollCosts_(summary), " \u20AC</span>\n                <span><i class=\"icon icon-diesel\"></i>").concat(_this.calculateGasolineCosts_(summary.dist), " \u20AC</span>\n              </div>\n              ").concat(showWithoutTraffic ? "<div class=\"traffic-time secondary\">".concat(normalizeTime(_this.mdcLogisticBreakTime_.selected ? summary.totaltime : summary.time), " ").concat(i18n('withoutTraffic'), "</div>") : '', "\n              ").concat(_this.mdcLogisticBreakTime_.selected
                    ? "<div class=\"traffic-time secondary\">".concat(normalizeTime(summary.breaktime, false), " ").concat(i18n('ofRest'), "</div>") : '', "\n              <div class=\"more-details\">").concat(i18n('moreDetails'), "</div>\n            </div>\n          </div>\n        "));
                jqRoute.on('click', function () {
                    _this.selectSummary(index);
                    _this.emit(EventType.SELECT_SUMMARY, index);
                });
                jqRoute.find('.more-details').on('click', function (e) {
                    e.preventDefault();
                    e.stopPropagation();
                    _this.emit(EventType.GET_REPORT, index);
                });
                _this.jqSummary_.append(jqRoute);
            });
        }
        else {
            this.jqSummary_.empty().hide();
        }
    };
    /**
     * @param {number} index Route index
     */
    VWidgetRouting.prototype.selectSummary = function (index) {
        this.jqSummary_.find('.route-alt.active').removeClass('active');
        this.jqSummary_.find(".route-alt:nth-child(".concat(index + 1, ")")).addClass('active');
    };
    /**
     * @param {boolean} enabled Enabled
     */
    VWidgetRouting.prototype.setLoading = function (enabled) {
        var jqProgressBar = this.jqElement_.find('.progressbar');
        if (enabled) {
            jqProgressBar.show();
        }
        else {
            jqProgressBar.hide();
        }
    };
    /**
     * @private
     * @param {import("../servers.js").RoutingResultRoute} route Route
     * @param {import("../control/NavBar.js").RoutingAutocompletes} routingAutocompletes Routing autocompletes
     */
    VWidgetRouting.prototype.setReportStops_ = function (route, routingAutocompletes) {
        var _a;
        var jqStopList = this.jqReportStops_.find('ul.stops');
        jqStopList.empty();
        jqStopList.append("<li><span class=\"label\">From: </span><span class=\"adr\">".concat(routingAutocompletes.origin.getValue(), "</span>"));
        routingAutocompletes.steps.forEach(function (stepAutocomplete) {
            jqStopList.append("<li><span class=\"label\">Through: </span><span class=\"adr\">".concat(stepAutocomplete.getValue(), "</span>"));
        });
        jqStopList.append("<li><span class=\"label\">To: </span><span class=\"adr\">".concat(routingAutocompletes.destination.getValue(), "</span>"));
        this.jqReportStops_.find('.dist .text').text(normalizeDistance(route.dist));
        if (this.mdcLogisticBreakTime_.selected) {
            this.jqReportStops_.find('.breaktime .text').text("".concat(normalizeTime(route.breaktime, false), " ").concat(i18n('ofRest')));
        }
        this.jqReportStops_.find('.time .text').text(normalizeTime(this.getTimeToShowFromSummary(route)));
        if (route.time !== route.realtime && !this.mdcDepartureTimeSwitch_.selected && (!this.mdcLogisticBreakTime_.selected || !((_a = this.logisticsParams_) === null || _a === void 0 ? void 0 : _a.rimp))) {
            this.jqReportStops_.find('.traffic').show();
            this.jqReportStops_
                .find('.traffic .text')
                .text("".concat(VWidgetRouting.normalizeTrafficTime_(route.time, route.realtime), " ").concat(i18n('routeStopsTrafficDelay')));
        }
        else {
            this.jqReportStops_.find('.traffic').hide();
            this.jqReportStops_.find('.traffic .text').text('');
        }
        this.jqReportStops_.find('.motorway .text').text("".concat(this.getTollCosts_(route), "Km"));
        this.jqReportStops_.find('.gas .text').text("".concat(this.calculateGasolineCosts_(route.dist), "\u20AC"));
    };
    /**
     * @param {boolean} drawn Drawn
     */
    VWidgetRouting.prototype.setRouteDrawn = function (drawn) {
        var btnCleanRoute = this.jqElement_.find('button.cleanRoute');
        var btnSaveRoute = this.jqElement_.find('button.saveRoute');
        var btnPrintRoute = this.jqElement_.find('button.printRoute').show();
        var btnRefresh = this.jqElement_.find('button.refresh').show();
        if (drawn) {
            btnCleanRoute.show();
            btnSaveRoute.show();
            btnPrintRoute.show();
            btnRefresh.show();
        }
        else {
            btnCleanRoute.hide();
            btnSaveRoute.hide();
            btnPrintRoute.hide();
            btnRefresh.hide();
        }
        this.routeDrawn_ = drawn;
    };
    /**
     * @param {import("../servers.js").RoutingResultRoute} data data
     * @param {import("../control/NavBar.js").RoutingAutocompletes} [routingAutocompletes] Routing autocmpmletes
     */
    VWidgetRouting.prototype.setReport = function (route, routingAutocompletes) {
        var _this = this;
        if (route !== null) {
            this.setReportStops_(route, routingAutocompletes);
            var stages = route.stages.stage;
            // Stages && substages
            stages.forEach(function (stage) {
                var currentSubStage = Array.isArray(stage.sub_stage) ? stage.sub_stage : [stage.sub_stage];
                currentSubStage.forEach(function (substage, index) {
                    var showSignals = VWidgetRouting.mustShowSignals_(substage);
                    var signals = showSignals ? (Array.isArray(substage.signal.data) ? substage.signal.data : [substage.signal.data]) : [];
                    var substageDataMouseEvent = /** @type {SubStageDataMouseEvent} */ ({
                        wkt: substage.wkt.value,
                        type: substage.type,
                        angle: substage.angle,
                    });
                    _this.jqReportSteps_.append($("\n            <li class=\"mdc-list-item vitem collection-item\">\n              <div class=\"arrow\">\n                ".concat(VWidgetRouting.hasExit_(substage) ? "\n                <div class=\"exit-road road ".concat(VWidgetRouting.normalizeSignalClass_(stage, substage, index), "\">\n                  <span class=\"icon icon-exit\"></span>\n                  <span>").concat(VWidgetRouting.getExit_(substage), "</span>\n                </div>") : " <span class=\"icon icon-".concat(VWidgetRouting.computeDirectionIcon_(substage.type, substage.angle), "\"></span>\n                "), "\n              </div>\n              <div class=\"step-detail\">\n                <div class=\"desc\">\n                  <span>").concat(VWidgetRouting.normalizeDesc_(substage.desc.value, substage.name), "</span>\n                </div>\n                ").concat(showSignals ? "\n                <div class=\"signals ".concat(VWidgetRouting.normalizeSignalClass_(stage, substage, index), "\">\n                  ").concat(signals.map(function (signal) { return (!VWidgetRouting.isExit_(signal.type) ? "<span>".concat(signal.value, "</span>") : ''); }).join(''), "\n                </div>\n                ") : '', "\n                <div class=\"timedist\">\n                  <span class=\"time\">").concat(substage.time.value, "</span>\n                  <span class=\"dist\">").concat(normalizeDistMeters(substage.dist_meters.value), "</span>\n                  <span class=\"sep\"></span>\n                </div>\n              </div>\n            </li>"))
                        .click(function () { return _this.emit(EventType.TAP_SUBSTAGE, substageDataMouseEvent); })
                        .mouseenter(function () { return _this.emit(EventType.MOUSEENTER_SUBSTAGE, substageDataMouseEvent); })
                        .mouseleave(function () { return _this.emit(EventType.MOUSELEAVE_SUBSTAGE, substageDataMouseEvent); }));
                });
            });
            this.jqOptions_.hide();
            this.jqSummary_.hide();
            this.jqReport_.show();
        }
        else {
            this.jqReportSteps_.empty();
            this.jqReport_.hide();
            this.jqOptions_.show();
        }
    };
    /**
     * @private
     */
    VWidgetRouting.prototype.adjustReportSize_ = function () {
        var viewport = this.getMapViewport();
        var mapHeight = viewport.offsetHeight;
        var routingOffset = 20;
        this.setMaxHeightReport(mapHeight - routingOffset);
    };
    /**
     * @private
     */
    VWidgetRouting.prototype.adjustOptionsSize_ = function () {
        var viewport = this.getMapViewport();
        var mapHeight = viewport.offsetHeight;
        var routingPlusTwoRouteOptionOffset = 120;
        var halfMoreSettings = 150;
        this.setMaxHeightOptions(mapHeight - routingPlusTwoRouteOptionOffset - halfMoreSettings);
    };
    /**
     * @param {number} height Height
     */
    VWidgetRouting.prototype.setMaxHeightSettings = function (height) {
        var jqMoreSettings = this.jqElement_.find('.more-settings');
        jqMoreSettings.css('max-height', height);
    };
    /**
     * @param {number} height Height
     */
    VWidgetRouting.prototype.setMaxHeightReport = function (height) {
        this.jqReport_.css('max-height', height);
    };
    /**
     * @param {number} height Height
     */
    VWidgetRouting.prototype.setMaxHeightOptions = function (height) {
        this.jqOptions_.css('max-height', height);
    };
    /**
     * @private
     * @param {import("../servers.js").RoutingResultRoute|null} summaryInfo Summary data
     * @return {string} Cost motorway
     */
    VWidgetRouting.prototype.calculateTollCosts_ = function (summaryInfo) {
        if (this.routingOptions_.carType === CarType.CAR) {
            return summaryInfo.coste_v1;
        }
        if (this.routingOptions_.carType === CarType.VAN) {
            return summaryInfo.coste_v2;
        }
        return summaryInfo.coste_v3;
    };
    /**
     * @private
     * @param {string} dist Distance
     * @return {string} Cost in €
     */
    VWidgetRouting.prototype.calculateGasolineCosts_ = function (dist) {
        return Number((this.routingOptions_.vehicleConsumption / 100) * parseFloat(dist) * this.routingOptions_.gasolinePrice).toFixed(2);
    };
    /**
     * @private
     * @param {import("../servers.js").RoutingResultRoute|null} summaryInfo Summary data
     * @return {string} costs
     */
    VWidgetRouting.prototype.getTollCosts_ = function (summaryInfo) {
        if (this.routingOptions_.carType === CarType.CAR) {
            return summaryInfo.coste_v1;
        }
        if (this.routingOptions_.carType === CarType.VAN) {
            return summaryInfo.coste_v2;
        }
        if (this.routingOptions_.carType === CarType.LOGISTICS) {
            return summaryInfo.coste_v3;
        }
        return null;
    };
    /**
     * @private
     * @static
     * @param {string} time Time format `hh:mm:ss`.
     * @param {string} realtime Time format `hh:mm:ss`.
     * @return {string} Readable time.
     */
    VWidgetRouting.normalizeTrafficTime_ = function (time, realtime) {
        var timeSec = hhmmssToSeconds(time);
        var realtimeSec = hhmmssToSeconds(realtime);
        var diff = realtimeSec - timeSec;
        if (diff > 30) {
            return "".concat(Math.round(diff / 60), " min");
        }
        return '0 min';
    };
    /**
     * @private
     * @static
     * @param {string} desc Description
     * @param {string} address Address
     * @return {string} Desc normalized
     */
    VWidgetRouting.normalizeDesc_ = function (desc, address) {
        if (desc.indexOf('<b>') !== -1) {
            desc = desc.replace(/<b>(.+?)<\/b>/g, "<b>".concat(address, "</b>")); // eslint-disable-line no-param-reassign
        }
        return desc;
    };
    /**
     * @private
     * @static
     * @param {import("../servers.js").RoutingResultSubstage} substage Substage
     * @return {string} Class
     */
    VWidgetRouting.normalizeRoadClass_ = function (substage) {
        var type = substage.type;
        var name = substage.name;
        if (type === 'A') {
            return 'blue';
        }
        if (type === 'J') {
            return name.startsWith('N-') ? 'red' : 'green';
        }
        if (type === 'I') {
            return 'yellow';
        }
        return 'white';
    };
    /**
     * @private
     * @param {Object} stage Stage
     * @param {Object} substage Substage
     * @param {number} index Index
     * @return {string} class
     */
    VWidgetRouting.normalizeSignalClass_ = function (stage, substage, index) {
        var substageAnt = stage.sub_stage[index - 1];
        if (substageAnt) {
            if (substageAnt.type === 'A') {
                return VWidgetRouting.normalizeRoadClass_(substageAnt);
            }
            return VWidgetRouting.normalizeRoadClass_(substage);
        }
        return VWidgetRouting.normalizeRoadClass_(substage);
    };
    /**
     * @private
     * @static
     * @param {Object} substage Substage
     * @return {boolean} `true` if substage has exit
     */
    VWidgetRouting.hasExit_ = function (substage) {
        if (substage.type !== 'A' && substage.signal) {
            var signals = substage.signal.data;
            for (var i = 0; i < signals.length; i += 1) {
                if (signals[i].type === '4E') {
                    return true;
                }
            }
        }
        return false;
    };
    /**
     * @private
     * @static
     * @param {Object} substage Substage
     * @return {string|null} Exit name
     */
    VWidgetRouting.getExit_ = function (substage) {
        if (substage.signal) {
            var signals = substage.signal.data;
            for (var i = 0; i < signals.length; i += 1) {
                if (signals[i].type === '4E') {
                    return signals[i].value;
                }
            }
        }
        return null;
    };
    /**
     * @private
     * @static
     * @param {string} signalType Signal type
     * @return {boolean} `true` if has exit.
     */
    VWidgetRouting.isExit_ = function (signalType) {
        return signalType && signalType === '4E';
    };
    /**
     * @private
     * @static
     * @param {Object} substage Substage
     * @return {boolean} Show signals or not
     */
    VWidgetRouting.mustShowSignals_ = function (substage) {
        // Show signals when 'autopista' or 'autovia'.
        if (substage.signal) {
            if (substage.type === 'A') {
                return true;
            }
            // Show signals only when is an exit.
            for (var i = 0; i < substage.signal.data.length; i += 1) {
                var data = substage.signal.data[i];
                if (data.type === '4E') {
                    return true;
                }
            }
        }
        return false;
    };
    /**
     * @private
     * @static
     * @param {string} type Type
     * @param {string} angle Angle
     * @return {string} Direction
     */
    VWidgetRouting.computeDirectionIcon_ = function (type, angle) {
        var nAngle = parseInt(angle, 10);
        if (type === 'R') {
            return 'roundabout';
        }
        if (type === 'G') {
            return 'ferry';
        }
        if (type === 'H') {
            return 'funicular';
        }
        if (nAngle > 314 && nAngle < 350) {
            return 'keep-right';
        }
        if (nAngle > 180 && nAngle < 315) {
            return 'turn-right';
        }
        if (nAngle > 10 && nAngle < 45) {
            return 'keep-left';
        }
        if (nAngle > 44 && nAngle < 180) {
            return 'turn-left';
        }
        return 'rectum';
    };
    /**
     * @param {GasolinePrices} gasolinePrices Gas station prices for routing widget
     */
    VWidgetRouting.prototype.updateGasolinePrices = function (gasolinePrices) {
        this.gasolinePrices_ = gasolinePrices;
    };
    /**
     * @param {import("../MapRouting").LogisticsParams} logisticsParams Logistics params
     */
    VWidgetRouting.prototype.updateLogisticsParams = function (logisticsParams) {
        this._updateLogisticParamInputValue('vweight', logisticsParams.vweight);
        this._updateLogisticParamInputValue('vaxleweight', logisticsParams.vaxleweight);
        this._updateLogisticParamInputValue('vheight', logisticsParams.vheight);
        this._updateLogisticParamInputValue('vlength', logisticsParams.vlength);
        this._updateLogisticParamInputValue('vwidth', logisticsParams.vwidth);
        this._updateLogisticParamInputValue('vmaxvel', logisticsParams.vmaxvel);
    };
    /**
     * @param {string} param Param
     * @param {string|number} value Value
     */
    VWidgetRouting.prototype._updateLogisticParamInputValue = function (param, value) {
        if (value !== undefined) {
            var input = this.jqElement_.find("input[type=text][name=".concat(param, "]"));
            input.val(value);
            var checkbox = this.jqElement_.find("input[type=checkbox][name=".concat(param, "]"));
            if (!checkbox[0].checked) {
                checkbox.trigger('click');
            }
        }
    };
    /**
     * Update Routing widget costs.
     * @param {number} consumption Consumption L/100 km. Example: `8.2`.
     * @param {string|undefined} gasType Gas Type. Available values: `gas95|gas98|diesel|biodiosel`.
     * @param {number|undefined} gasPrice Gasoline price. €/L. Example: `1.51`.
     */
    VWidgetRouting.prototype.setAddedCosts = function (consumption, gasType, gasPrice) {
        this.routingOptions_.vehicleConsumption = consumption;
        this.mdcInputConsum_.value = consumption;
        this.routingOptions_.gasolineSelected = gasType;
        this.jqElement_.find("input[name=gas][value=".concat(gasType, "]")).trigger('click');
        this.routingOptions_.gasolinePrice = gasPrice || parseFloat(this.gasolinePrices_[this.routingOptions_.gasolineSelected]);
        this.mdcInputGasPrice_.value = this.routingOptions_.gasolinePrice;
    };
    return VWidgetRouting;
}(BaseView));
export default VWidgetRouting;
//# sourceMappingURL=VWidgetRouting.js.map